import { timestampFromMs } from '@bufbuild/protobuf/wkt';
import {
  CorrelationAckState,
  CorrelationStatus
} from '@kentik/schema/gen/ts/kentik/alerting/correlation/v202609/correlation_pb';
import { Severity } from '@kentik/schema/gen/ts/kentik/alerting/types/v202303/types_pb';
import { getStartAndEndFromLookback } from 'core/util/dateUtils';
import type { CorrelationFilterInit } from './correlationsApi';
import { CONFIDENCE_LEVEL_BY_BAND, DEFAULT_LOOKBACK_SECONDS } from './constants';
import type { CorrelationFilterValues, TimeSelection } from './types';

function splitIds(value?: string): string[] {
  return (value ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

/**
 * The window correlations must have been active in: the custom range, or the lookback up to now. Dates are
 * coerced because the filter sidebar restores them from the URL as moments.
 */
export function activeRange({ lookback, startDate, endDate }: CorrelationFilterValues): TimeSelection {
  if (startDate && endDate) {
    return { start: Number(startDate), end: Number(endDate) };
  }
  const { start, end } = getStartAndEndFromLookback(lookback ?? DEFAULT_LOOKBACK_SECONDS, 0);
  return { start: start.valueOf(), end: end.valueOf() };
}

export function toFilter(values: CorrelationFilterValues): CorrelationFilterInit {
  const { statuses, ackStates, severityLabels, confidenceBands } = values;
  const alertIds = splitIds(values.alertIds);
  const correlationIds = splitIds(values.correlationIds);
  const { start, end } = activeRange(values);
  const filter: CorrelationFilterInit = {
    active: { start: timestampFromMs(start), end: timestampFromMs(end) }
  };

  if (statuses?.length) {
    filter.statuses = statuses.map((status) =>
      status === 'cleared' ? CorrelationStatus.CLOSED : CorrelationStatus.OPEN
    );
  }
  if (ackStates?.length) {
    filter.ackStates = ackStates.map((ackState) =>
      ackState === 'acked' ? CorrelationAckState.ACKED : CorrelationAckState.NOT_ACKED
    );
  }
  if (severityLabels?.length) {
    filter.severities = severityLabels
      .map((label) => Severity[label.toUpperCase() as keyof typeof Severity])
      .filter((severity) => severity !== undefined);
  }
  if (confidenceBands?.length) {
    filter.confidenceLevels = confidenceBands.map((band) => CONFIDENCE_LEVEL_BY_BAND[band]);
  }
  if (values.createdFrom && values.createdTo) {
    // The server's range is inclusive at both ends; the timeline's buckets end exclusively.
    filter.opened = {
      start: timestampFromMs(Number(values.createdFrom)),
      end: timestampFromMs(Number(values.createdTo) - 1)
    };
  }
  if (alertIds.length) {
    filter.alertIds = alertIds;
  }
  if (correlationIds.length) {
    filter.correlationIds = correlationIds;
  }

  return filter;
}
